import React, { useEffect, useMemo, useState } from "react";
import DealerControls from "./table/DealerControls";

function pad2(n) {
  const x = Math.max(0, Math.trunc(Number(n) || 0));
  return x < 10 ? `0${x}` : String(x);
}

function fmtMmSs(totalSec) {
  const s = Math.max(0, Math.trunc(Number(totalSec) || 0));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${pad2(ss)}`;
}

export default function PokerTableHeaderBar({
  tableCode,
  joined,
  game,
  actionSeat,
  mySeatNo,
  mySt,

  tournamentComplete,
  tournamentWinnerSeat,

  tableView,
  setTableView,

  doToggleSitOut,
  setConfirmLeaveOpen,

  isDealer,
  dealerNext,
  nav,

  // Tournament admin controls (optional callbacks)
  onTourneyPause,
  onTourneyResume,
  onTourneyNextLevel,
  onTourneyPrevLevel,
  onTourneyResetLevel,
}) {
  const t = game?.tournament || null;
  const isTourney = game?.settings?.mode === "tournament" && !!t;

  const levels = t?.levels || [];
  const levelIdx = Number(t?.currentLevelIndex || 0);
  const level = levels[levelIdx] || null;
  const levelsCount = levels.length || 0;

  // ✅ Official/effective blinds are from game.settings (applied between hands)
  const appliedIdx = Number.isFinite(Number(t?.appliedLevelIndex)) ? Number(t.appliedLevelIndex) : levelIdx;
  const appliedLevel = levels[appliedIdx] || null;

  const isPaused = !!t?.paused;
  const isLastLevel = !!t?.isLastLevel || (levelsCount > 0 && levelIdx >= levelsCount - 1);

  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => {
    if (!isTourney || !t?.running) return;
    const id = setInterval(() => setNowMs(Date.now()), 500);
    return () => clearInterval(id);
  }, [isTourney, t?.running]);

  const remainingSec = useMemo(() => {
    if (!isTourney || !t?.running) return null;

    const durSec = Math.max(1, Number(t?.levelDurationSec || 0));
    const durMs = durSec * 1000;

    if (isPaused) {
      const remMs = Number(t?.remainingMs);
      if (Number.isFinite(remMs)) return Math.ceil(Math.max(0, remMs) / 1000);
    }

    const startedAt = Number(t?.levelStartedAt || 0);
    if (!startedAt) return null;

    const elapsedMs = Math.max(0, nowMs - startedAt);
    const remMs = Math.max(0, durMs - elapsedMs);
    return Math.ceil(remMs / 1000);
  }, [isTourney, t?.running, t?.levelDurationSec, t?.levelStartedAt, t?.remainingMs, isPaused, nowMs]);

  const showTourneyStrip = isTourney && t?.running && !tournamentComplete;

  const showNext = isTourney && levelsCount > 0 && levelIdx !== appliedIdx;
  const officialSb = game?.settings?.sb ?? "-";
  const officialBb = game?.settings?.bb ?? "-";
  const nextSb = level?.sb ?? "-";
  const nextBb = level?.bb ?? "-";

  return (
    <div
      className="poker-card"
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 14,
      }}
    >
      <div style={{ minWidth: 220 }}>
        <div style={{ fontSize: 18, fontWeight: 900 }}>Stół: {tableCode}</div>
        <div style={{ opacity: 0.8, fontSize: 13 }}>
          {joined ? "Połączono" : "Łączenie…"} · street: <b>{game?.street || "-"}</b> · dealer: <b>{game?.dealerSeat ?? "-"}</b> · action:{" "}
          <b>{actionSeat ?? "-"}</b>
        </div>

        {showTourneyStrip ? (
          <div
            style={{
              marginTop: 8,
              padding: "8px 10px",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.14)",
              display: "flex",
              gap: 10,
              alignItems: "center",
              flexWrap: "wrap",
              rowGap: 6,
              maxWidth: 560,
            }}
          >
            <div style={{ fontWeight: 900 }}>
              Level{" "}
              <span style={{ color: "rgba(255,165,0,0.95)" }}>
                {levelsCount ? `${levelIdx + 1}/${levelsCount}` : levelIdx + 1}
              </span>
            </div>

            <div style={{ opacity: 0.85 }}>
              Aktualne SB/BB:{" "}
              <b style={{ color: "rgba(255,165,0,0.95)" }}>
                {officialSb}/{officialBb}
              </b>
              {showNext ? (
                <span style={{ marginLeft: 10, opacity: 0.9 }}>
                  Następne:{" "}
                  <b style={{ color: "rgba(255,165,0,0.95)" }}>
                    {nextSb}/{nextBb}
                  </b>
                </span>
              ) : null}
            </div>

            <div style={{ opacity: 0.85 }}>
              Czas:{" "}
              <b style={{ color: "rgba(255,165,0,0.95)" }}>
                {remainingSec == null ? "-" : fmtMmSs(remainingSec)}
              </b>
              {isPaused ? <span style={{ marginLeft: 6, opacity: 0.9 }}>(pauza)</span> : null}
              {isLastLevel ? <span style={{ marginLeft: 6, opacity: 0.9 }}>(ostatni)</span> : null}
            </div>

            {isDealer ? (
              <div style={{ display: "flex", gap: 8, marginLeft: "auto", flexWrap: "wrap", rowGap: 8 }}>
                {!isPaused ? (
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => onTourneyPause?.()}
                    disabled={!onTourneyPause}
                    title="Pauza zegara turniejowego (tylko dealer/host)."
                  >
                    Pauza
                  </button>
                ) : (
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => onTourneyResume?.()}
                    disabled={!onTourneyResume}
                    title="Wznów zegar turniejowy (tylko dealer/host)."
                  >
                    Wznów
                  </button>
                )}

                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => onTourneyPrevLevel?.()}
                  disabled={!onTourneyPrevLevel || levelIdx <= 0}
                  title="Cofanie jak kaseta: 1 klik = pauza + reset do początku poziomu, 2 klik (na początku) = cofnięcie levelu."
                >
                  ◀ Poziom
                </button>

                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => onTourneyNextLevel?.()}
                  disabled={!onTourneyNextLevel || isLastLevel}
                  title="Przejdź na kolejny poziom (tylko dealer/host). Blindy wejdą od następnej ręki."
                >
                  Poziom ▶
                </button>

                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => onTourneyResetLevel?.()}
                  disabled={!onTourneyResetLevel}
                  title="Reset czasu bieżącego poziomu + pauza."
                >
                  Reset
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {tournamentComplete ? (
          <div style={{ marginTop: 6, fontSize: 13, fontWeight: 900, color: "rgba(255,165,0,0.95)" }}>
            🏆 Gra zakończona — zwycięzca: <span style={{ color: "inherit" }}>Seat {tournamentWinnerSeat ?? "-"}</span>
          </div>
        ) : null}
      </div>

      <div
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          rowGap: 10,
          maxWidth: 560,
        }}
      >
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            className={`btn ${tableView === "visual" ? "btn-primary" : "btn-secondary"}`}
            type="button"
            onClick={() => setTableView("visual")}
            title="Widok stołu"
          >
            Stół
          </button>
          <button
            className={`btn ${tableView === "list" ? "btn-primary" : "btn-secondary"}`}
            type="button"
            onClick={() => setTableView("list")}
            title="Widok listy + panel akcji"
          >
            Lista
          </button>
        </div>

        <button
          className="btn btn-secondary"
          type="button"
          disabled={mySeatNo == null}
          onClick={doToggleSitOut}
          title={mySt?.sittingOut ? "Wracasz do gry na swoim miejscu." : "Wstajesz od stołu: żetony zostają, a system będzie auto-foldował."}
        >
          {mySt?.sittingOut ? "Usiądź" : "Wstań"}
        </button>

        <button
          className="btn btn-secondary"
          type="button"
          onClick={() => setConfirmLeaveOpen(true)}
          title="Wyjście z gry: znikasz ze stołu (cashout)."
        >
          Wyjdź
        </button>

        <DealerControls isDealer={isDealer} dealerNext={dealerNext} onLobby={() => nav("/poker")} />
      </div>
    </div>
  );
}